/**
 * Load the data needed to render the Job Launch drawer contents.
 */

// @todo: Migrate to 'Dialog.Alert' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Alert} from '@wandb/weave/components/Alert';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useMemo} from 'react';

import {useSecretsQuery} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {createUserIdFilter} from '../filterUtil';
import {
  useAvailableJobOptions,
  useProjectView,
  useViewerQueueOptions,
} from '../hooks';
import {useJobDetails, useJobDetailsNoPoll} from '../JobDetail/hooks';
import {type JobDetails, type JobRun} from '../JobDetail/utilTypes';
import {type JobOption, SelectJob} from '../SelectJob';
import {SelectJobVersion} from '../SelectJobVersion';
import {TruncatedMarkdown} from '../TruncatedMarkdown';
import {type LaunchSpec} from '../utilTypes';
import {useJobDrawerConfigImporter} from './hooks';
import {JobDrawerConfigImporter} from './JobDrawerConfigImporter';
import * as S from './JobDrawerContent.styles';
import {
  getVersionDescription,
  JobDrawerContentInner,
} from './JobDrawerContentInner';
import {JobDrawerHeader} from './JobDrawerHeader';
import {JobDrawerSecretsContext} from './JobDrawerSecretsContext';
import {JobDrawerSecretsSubview} from './JobDrawerSecretsSubview';
import {useJobDrawerState} from './JobDrawerStateContext';

// Jobs with this access type are considered private
const PRIVATE_ACCESS_TERMS = ['ENTITY_READ', 'PRIVATE'];

enum JobDrawerView {
  Main = 'MAIN',
  ConfigImporter = 'CONFIG_IMPORTER',
  Secrets = 'SECRETS',
}

interface ViewConfig {
  title: string;
  showBackButton: boolean;
}

const getViewConfig = (view: JobDrawerView): ViewConfig => {
  switch (view) {
    case JobDrawerView.Main:
      return {
        title: 'Configure launch',
        showBackButton: false,
      };
    case JobDrawerView.ConfigImporter:
      return {
        title: '',
        showBackButton: true,
      };
    case JobDrawerView.Secrets:
      return {
        title: '',
        showBackButton: true,
      };
  }
};

export type JobDrawerContentProps = React.PropsWithChildren<{
  // For passing, e.g., scheduler selection when opened from sweeps-on-launch surface.
  // These should be rendered at the bottom under an "Additional Settings" section
  // Parent fully owns these components and their state.
  // children: React.ReactNode;
  //

  closeDrawer: () => void;

  entityName: string;
  projectName: string;

  open: boolean;

  // Allow preselection of job / version / overrides, e.g., when we are
  // embedded somewhere that already fetched this info.
  defaultJobId?: string;
  defaultJobVersion?: string;
  defaultRunToClone?: string;

  // optional hint text underneath override bin
  overrideHintText?: string;

  finalizeCTA?: string; // Bottom right button text
  onFinalize?: (spec: LaunchSpec) => void; // Default: throws

  onlyLoadUserRuns?: boolean;
}>;

export const JobDrawerContent = ({
  entityName,
  projectName,
  defaultJobId,
  defaultJobVersion,
  defaultRunToClone,
  overrideHintText,
  finalizeCTA,
  closeDrawer,
  onFinalize,
  onlyLoadUserRuns = true,
  children,
  open,
}: JobDrawerContentProps) => {
  const {jobOptions: options} = useAvailableJobOptions(entityName, projectName);

  const [jobId, setJobId] = React.useState<JobOption | undefined>();
  const userSelectedJobId = React.useRef(false);
  const [currentView, setCurrentView] = React.useState<JobDrawerView>(
    JobDrawerView.Main
  );

  const {targetEntityName, setTargetEntityName} = useJobDrawerState();

  const viewer = useViewer();
  const {
    data: secretsDataResult,
    loading: secretsLoading,
    error: secretsError,
    refetch: refetchSecrets,
  } = useSecretsQuery({
    variables: {
      entityName: targetEntityName || '',
    },
    skip: !targetEntityName,
  });

  useEffect(() => {
    // HACK: reset userSelectedJobId when the drawer is closed
    // so that the defaultJobId is used when the drawer is reopened
    if (!open) {
      userSelectedJobId.current = false;
    }
  }, [open]);

  // useEffect to capture setState change after options are loaded
  // since options are loaded incrementally, this can run several times
  // and selects the default option as soon as it is available. However,
  // it's possible the user selected a different option before the default
  // option was loaded, so we only set the default if the user hasn't
  // already selected a job.
  useEffect(() => {
    if (!userSelectedJobId.current && defaultJobId != null) {
      const optionToSelect = options.find(o => o.value === defaultJobId);
      if (optionToSelect != null) {
        setJobId(optionToSelect);
      }
    }
  }, [defaultJobId, options]);

  const [jobVersion, setJobVersion] = React.useState<string>(
    defaultJobVersion ?? ''
  );
  // This is required to make the correct default job version appear
  // on first load when cloning a run.
  useEffect(() => {
    setJobVersion(defaultJobVersion ?? '');
  }, [defaultJobVersion]);

  // we only load up runs from all users (that the viewer has access to) if
  // loading from the run overview page since it goes thru a different code path
  // using the previous runs from the job.

  // otherwise, load up to 50 runs for each job version from the current user.
  // todo(dom): this should use the same code path as rerun from the runs table.
  const {job, loading, error} = useJobDetails(
    jobId?.value ?? '',
    jobVersion,
    onlyLoadUserRuns ? createUserIdFilter(viewer?.id ?? '') : undefined,
    onlyLoadUserRuns ? 50 : undefined
  );

  const {setConfigSourceRun, configSourceRun, defaultConfigSourceRun} =
    useJobDrawerConfigImporter({
      job,
      defaultConfigSourceRunName: defaultRunToClone,
    });

  const importerColors = useProjectView(entityName, projectName);

  const viewConfig = getViewConfig(currentView);

  const value = useMemo(
    () => ({
      secretsData: secretsDataResult?.entity?.secrets ?? [],
      secretsLoading: secretsLoading,
      secretsError: !!secretsError,
      refetchSecrets: refetchSecrets,
    }),
    [
      secretsDataResult?.entity?.secrets,
      secretsLoading,
      secretsError,
      refetchSecrets,
    ]
  );

  if (error) {
    return <Alert severity="error">Error loading job: {error.message}</Alert>;
  }

  return (
    <JobDrawerSecretsContext.Provider value={value}>
      <S.JobDrawer>
        <S.StickyHeader>
          <JobDrawerHeader
            title={viewConfig.title}
            showBackButton={viewConfig.showBackButton}
            onBack={() => setCurrentView(JobDrawerView.Main)}
            onClose={() => closeDrawer()}
          />
        </S.StickyHeader>
        <Tailwind>
          <div className="mx-[-30px] relative pointer-events-none border-b border-moon-250" />
        </Tailwind>
        <S.JobDrawerMain data-test="job-drawer-main">
          {currentView === JobDrawerView.ConfigImporter && (
            <JobDrawerConfigImporter
              onCancel={() => setCurrentView(JobDrawerView.Main)}
              onImport={() => {
                setCurrentView(JobDrawerView.Main);
              }}
              setConfigSourceRun={setConfigSourceRun}
              configSourceRun={configSourceRun}
              customRunColors={importerColors.customRunColors}
              defaultConfigSourceRun={defaultConfigSourceRun}
              job={job}
            />
          )}
          {currentView === JobDrawerView.Secrets && (
            <JobDrawerSecretsSubview
              entityName={targetEntityName ?? ''}
              onClose={() => setCurrentView(JobDrawerView.Main)}
            />
          )}
          {currentView === JobDrawerView.Main && (
            <>
              <S.JobDrawerJobSelectionContent>
                <S.LabelRow>
                  <S.Label>Job</S.Label>
                </S.LabelRow>
                <SelectJob
                  entityName={entityName}
                  projectName={projectName}
                  selectedJobId={jobId ?? null}
                  onSelectJob={alias => {
                    userSelectedJobId.current = true;
                    setJobId(alias);
                    setJobVersion(defaultJobVersion ?? '');
                  }}
                />
              </S.JobDrawerJobSelectionContent>

              {jobId != null && (
                <JobDrawerVersionContent
                  jobId={jobId.value}
                  jobVersion={jobVersion}
                  defaultRunToClone={defaultRunToClone}
                  // Job options loaded from the project won't have an entity set,
                  // so use the entity of the JobDrawerContent
                  entityName={jobId.entity ?? entityName}
                  projectName={jobId.project ?? projectName}
                  overrideHintText={overrideHintText}
                  finalizeCTA={finalizeCTA}
                  setJobVersion={setJobVersion}
                  closeDrawer={() => {
                    closeDrawer();
                  }}
                  onFinalize={onFinalize}
                  openConfigImporter={() =>
                    setCurrentView(JobDrawerView.ConfigImporter)
                  }
                  openSecretsView={() => setCurrentView(JobDrawerView.Secrets)}
                  onTargetEntityChange={setTargetEntityName}
                  job={job ?? null}
                  jobLoading={loading}
                  configSourceRun={configSourceRun}
                  defaultConfigSourceRun={defaultConfigSourceRun}
                  setConfigSourceRun={setConfigSourceRun}>
                  {children}
                </JobDrawerVersionContent>
              )}
            </>
          )}
        </S.JobDrawerMain>
      </S.JobDrawer>
    </JobDrawerSecretsContext.Provider>
  );
};

type JobDrawerVersionContentProps = React.PropsWithChildren<{
  entityName: string;
  projectName: string;
  jobId: string;
  jobVersion: string;
  defaultRunToClone?: string;
  finalizeCTA?: string;
  overrideHintText?: string;
  onFinalize?: (spec: LaunchSpec) => void;
  setJobVersion: (alias: string) => void;
  closeDrawer: () => void;
  openConfigImporter: () => void;
  openSecretsView: () => void;
  onTargetEntityChange?: (entityName: string) => void;
  job: JobDetails | null;
  jobLoading: boolean;
  configSourceRun: JobRun | null;
  defaultConfigSourceRun: JobRun | null;
  setConfigSourceRun: (run: JobRun | null) => void;
}>;

const JobDrawerVersionContent = (props: JobDrawerVersionContentProps) => {
  if (props.job == null || props.jobLoading) {
    return <Loader active name="job-drawer-version-content-loader" />;
  }

  const versionDescription = getVersionDescription(
    props.job.versions ?? [],
    props.job.versionInfo.runTarget ?? ''
  );

  return (
    <>
      <S.JobDrawerJobSelectionContent>
        <S.LabelRow>
          <S.Label>Job version</S.Label>
        </S.LabelRow>
        <SelectJobVersion
          jobVersions={props.job.versions}
          selectedAlias={props.jobVersion}
          onSelectJobAlias={props.setJobVersion}
        />
        {versionDescription && (
          <>
            <S.LabelRow>
              <S.Label>Version description</S.Label>
            </S.LabelRow>
            <S.VersionDescription>
              <TruncatedMarkdown
                content={versionDescription}
                maxLength={280}
                tooltipPosition="left center"
              />
            </S.VersionDescription>
          </>
        )}
      </S.JobDrawerJobSelectionContent>

      {props.jobVersion != null && (
        <JobDrawerConfigureContent
          entityName={props.entityName}
          projectName={props.projectName}
          job={props.job}
          defaultRunToClone={props.defaultRunToClone}
          overrideHintText={props.overrideHintText}
          finalizeCTA={props.finalizeCTA}
          onFinalize={props.onFinalize}
          closeDrawer={props.closeDrawer}
          openConfigImporter={props.openConfigImporter}
          openSecretsView={props.openSecretsView}
          onTargetEntityChange={props.onTargetEntityChange}
          configSourceRun={props.configSourceRun}
          defaultConfigSourceRun={props.defaultConfigSourceRun}
          setConfigSourceRun={props.setConfigSourceRun}>
          {props.children}
        </JobDrawerConfigureContent>
      )}
    </>
  );
};

type JobDrawerConfigureContentProps = React.PropsWithChildren<{
  entityName: string;
  projectName: string;
  renderLaunchButton?: boolean;
  job: JobDetails;
  defaultRunToClone?: string;
  lockedRunToClone?: boolean;
  finalizeCTA?: string;
  overrideHintText?: string;
  onFinalize?: (spec: LaunchSpec) => void;
  closeDrawer: () => void;
  openConfigImporter: () => void;
  openSecretsView: () => void;
  onTargetEntityChange?: (entityName: string) => void;
  configSourceRun: JobRun | null;
  defaultConfigSourceRun: JobRun | null;
  setConfigSourceRun: (run: JobRun | null) => void;
}>;

const JobDrawerConfigureContent = (props: JobDrawerConfigureContentProps) => {
  const jobIsPrivate: boolean =
    !props.job.projectAccess ||
    PRIVATE_ACCESS_TERMS.includes(props.job.projectAccess);
  const queryQueues = useViewerQueueOptions(jobIsPrivate, props.entityName);

  const queryColors = useProjectView(props.entityName, props.projectName);

  if (queryColors.error || queryQueues.error) {
    return <Alert severity="error">Error fetching job details</Alert>;
  }
  if (queryColors.loading || queryQueues.loading) {
    return <Loader active name="job-drawer-configure-content-loader" />;
  }
  if (props.job.versions.length === 0) {
    // At the moment we are disabling the launch button when this condition
    // occurs, so we don't expect to get here.
    return <Alert severity="info">This job has no versions to launch</Alert>;
  }

  return (
    <JobDrawerContentInner
      entityName={props.entityName}
      projectName={props.projectName}
      job={props.job}
      runToClone={props.defaultRunToClone}
      lockedRunToClone={props.lockedRunToClone}
      customRunColors={queryColors.customRunColors}
      queues={queryQueues.queues}
      finalizeCTA={props.finalizeCTA}
      overrideHintText={props.overrideHintText}
      onFinalize={props.onFinalize}
      onClose={props.closeDrawer}
      openConfigImporter={props.openConfigImporter}
      openSecretsView={props.openSecretsView}
      onTargetEntityChange={props.onTargetEntityChange}
      configSourceRun={props.configSourceRun}
      defaultConfigSourceRun={props.defaultConfigSourceRun}
      setConfigSourceRun={props.setConfigSourceRun}>
      {props.children}
    </JobDrawerContentInner>
  );
};

// A version of the same job drawer with the job and version locked.
export type JobDrawerContentLockedProps = React.PropsWithChildren<{
  closeDrawer: () => void;

  entityName: string;
  projectName: string;

  // required preselection of jobID and jobVersion in this LOCKED drawer
  jobId: string;
  jobVersion: string;

  defaultRunToClone?: string;

  // optional hint text underneath override bin
  overrideHintText?: string;

  finalizeCTA?: string; // Bottom right button text
  onFinalize?: (spec: LaunchSpec) => void; // Default: throws
}>;

export const JobDrawerContentLocked = ({
  entityName,
  projectName,
  jobId,
  jobVersion,
  defaultRunToClone,
  overrideHintText,
  finalizeCTA,
  closeDrawer,
  onFinalize,
  children,
}: JobDrawerContentLockedProps) => {
  const {job, loading, error} = useJobDetailsNoPoll(jobId, jobVersion);
  const [currentView, setCurrentView] = React.useState<JobDrawerView>(
    JobDrawerView.Main
  );

  const {targetEntityName, setTargetEntityName} = useJobDrawerState();

  const {setConfigSourceRun, configSourceRun, defaultConfigSourceRun} =
    useJobDrawerConfigImporter({
      job,
      defaultConfigSourceRunName: defaultRunToClone,
    });

  const {
    data: secretsDataResult,
    loading: secretsLoading,
    error: secretsError,
    refetch: refetchSecrets,
  } = useSecretsQuery({
    variables: {
      entityName: targetEntityName || '',
    },
    skip: !targetEntityName,
  });

  const importerColors = useProjectView(entityName, projectName);

  const value = useMemo(
    () => ({
      secretsData: secretsDataResult?.entity?.secrets ?? [],
      secretsLoading: secretsLoading,
      secretsError: !!secretsError,
      refetchSecrets: refetchSecrets,
    }),
    [
      secretsDataResult?.entity?.secrets,
      secretsLoading,
      secretsError,
      refetchSecrets,
    ]
  );

  if (loading || !jobId) {
    return <Loader active name="job-drawer-content-locked-loader" />;
  }

  if (error) {
    return <Alert severity="error">Error loading job: {error.message}</Alert>;
  }

  if (job == null) {
    return <Alert severity="error">Error loading job</Alert>;
  }

  const jobOption = {
    value: job?.id,
    label: job?.name ?? '',
    entity: job.entityName,
    project: job.projectName,
  } as JobOption;

  const viewConfig = getViewConfig(currentView);

  return (
    <JobDrawerSecretsContext.Provider value={value}>
      <S.JobDrawer>
        <S.StickyHeader>
          <JobDrawerHeader
            title={
              currentView === JobDrawerView.Main
                ? `Launch ${jobOption?.label}`
                : viewConfig.title
            }
            showBackButton={viewConfig.showBackButton}
            onBack={() => setCurrentView(JobDrawerView.Main)}
            onClose={() => closeDrawer()}
          />
        </S.StickyHeader>
        <Tailwind>
          <div className="mx-[-30px] relative pointer-events-none border-b border-moon-250" />
        </Tailwind>
        <S.JobDrawerMain data-test="job-drawer-main">
          {currentView === JobDrawerView.ConfigImporter && (
            <JobDrawerConfigImporter
              onCancel={() => setCurrentView(JobDrawerView.Main)}
              setConfigSourceRun={setConfigSourceRun}
              configSourceRun={configSourceRun}
              onImport={() => {
                setCurrentView(JobDrawerView.Main);
              }}
              customRunColors={importerColors.customRunColors}
              defaultConfigSourceRun={defaultConfigSourceRun}
              job={job}
            />
          )}
          {currentView === JobDrawerView.Secrets && (
            <JobDrawerSecretsSubview
              entityName={targetEntityName}
              onClose={() => setCurrentView(JobDrawerView.Main)}
            />
          )}
          {currentView === JobDrawerView.Main && (
            <>
              <S.JobDrawerJobSelectionContent>
                <S.LabelRow>
                  <S.Label>Job</S.Label>
                </S.LabelRow>
                <SelectJob
                  entityName={job.entityName}
                  projectName={job.projectName}
                  selectedJobId={jobOption ?? null}
                  onSelectJob={() => {}}
                  disabled={true}
                />
              </S.JobDrawerJobSelectionContent>

              <JobDrawerVersionContentLocked
                job={job}
                jobAlias={jobVersion}
                defaultRunToClone={defaultRunToClone}
                entityName={job.entityName}
                projectName={job.projectName}
                overrideHintText={overrideHintText}
                finalizeCTA={finalizeCTA}
                closeDrawer={closeDrawer}
                onFinalize={onFinalize}
                openConfigImporter={() =>
                  setCurrentView(JobDrawerView.ConfigImporter)
                }
                openSecretsView={() => setCurrentView(JobDrawerView.Secrets)}
                onTargetEntityChange={setTargetEntityName}
                configSourceRun={configSourceRun}
                defaultConfigSourceRun={defaultConfigSourceRun}
                setConfigSourceRun={setConfigSourceRun}>
                {children}
              </JobDrawerVersionContentLocked>
            </>
          )}
        </S.JobDrawerMain>
      </S.JobDrawer>
    </JobDrawerSecretsContext.Provider>
  );
};

type JobDrawerVersionContentLockedProps = React.PropsWithChildren<{
  entityName: string;
  projectName: string;
  job: JobDetails;
  jobAlias: string;
  defaultRunToClone?: string;
  finalizeCTA?: string;
  overrideHintText?: string;
  onFinalize?: (spec: LaunchSpec) => void;
  closeDrawer: () => void;
  openConfigImporter: () => void;
  openSecretsView: () => void;
  onTargetEntityChange?: (entityName: string) => void;
  configSourceRun: JobRun | null;
  defaultConfigSourceRun: JobRun | null;
  setConfigSourceRun: (run: JobRun | null) => void;
}>;

const JobDrawerVersionContentLocked = (
  props: JobDrawerVersionContentLockedProps
) => {
  const versionDescription = getVersionDescription(
    props.job.versions,
    props.job.versionInfo.runTarget ?? ''
  );

  return (
    <>
      <S.JobDrawerJobSelectionContent>
        <S.LabelRow>
          <S.Label>Job version</S.Label>
        </S.LabelRow>
        <SelectJobVersion
          jobVersions={props.job.versions}
          selectedAlias={props.jobAlias}
          onSelectJobAlias={() => {}}
          disabled={true}
        />

        {versionDescription && (
          <>
            <S.LabelRow>
              <S.Label>Version description</S.Label>
            </S.LabelRow>
            <S.VersionDescription>
              <TruncatedMarkdown
                content={versionDescription}
                maxLength={280}
                tooltipPosition="left center"
              />
            </S.VersionDescription>
          </>
        )}
      </S.JobDrawerJobSelectionContent>
      <JobDrawerConfigureContent
        entityName={props.entityName}
        projectName={props.projectName}
        job={props.job}
        lockedRunToClone={true}
        defaultRunToClone={props.defaultRunToClone}
        overrideHintText={props.overrideHintText}
        finalizeCTA={props.finalizeCTA}
        onFinalize={props.onFinalize}
        closeDrawer={props.closeDrawer}
        openConfigImporter={props.openConfigImporter}
        openSecretsView={props.openSecretsView}
        onTargetEntityChange={props.onTargetEntityChange}
        configSourceRun={props.configSourceRun}
        defaultConfigSourceRun={props.defaultConfigSourceRun}
        setConfigSourceRun={props.setConfigSourceRun}>
        {props.children}
      </JobDrawerConfigureContent>
    </>
  );
};
